import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { useToast } from '@/hooks/use-toast';

export interface SyncStatus {
  id: string;
  user_id: string;
  sync_type: 'strava' | 'manual' | 'background';
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  progress_percentage: number;
  total_activities: number;
  processed_activities: number;
  error_message?: string;
  started_at: string;
  completed_at?: string;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface RealtimeSyncState {
  currentSync: SyncStatus | null;
  syncHistory: SyncStatus[];
  isLoading: boolean;
  error: string | null;
}

export const useRealtimeSync = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [state, setState] = useState<RealtimeSyncState>({
    currentSync: null,
    syncHistory: [],
    isLoading: true,
    error: null,
  });

  // Fetch current sync status
  const fetchSyncStatus = useCallback(async () => {
    if (!user?.id) return;

    try {
      // Get current active sync
      const { data: currentSync, error: currentError } = await supabase
        .from('sync_status')
        .select('*')
        .eq('user_id', user.id)
        .in('status', ['pending', 'in_progress'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (currentError) throw currentError;

      // Get recent sync history
      const { data: syncHistory, error: historyError } = await supabase
        .from('sync_status')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (historyError) throw historyError;

      setState(prev => ({
        ...prev,
        currentSync: (currentSync as SyncStatus) || null,
        syncHistory: (syncHistory as SyncStatus[]) || [],
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error('Error fetching sync status:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch sync status',
      }));
    }
  }, [user?.id]);

  // Start a new sync
  const startSync = useCallback(async (syncType: 'strava' | 'manual' | 'background') => {
    if (!user?.id) return null;

    try {
      const { data, error } = await supabase
        .from('sync_status')
        .insert({
          user_id: user.id,
          sync_type: syncType,
          status: 'pending',
          progress_percentage: 0,
          total_activities: 0,
          processed_activities: 0,
          metadata: {
            initiated_from: window.location.pathname,
            user_agent: navigator.userAgent,
          },
        })
        .select()
        .single();

      if (error) throw error;

      // Trigger the actual sync via edge function
      if (syncType === 'strava') {
        const { error: syncError } = await supabase.functions.invoke('sync-strava-activities', {
          body: { sync_id: data.id },
        });

        if (syncError) {
          console.error('Error triggering sync:', syncError);
          // Update sync status to failed
          await supabase
            .from('sync_status')
            .update({
              status: 'failed',
              error_message: syncError.message,
              completed_at: new Date().toISOString(),
            })
            .eq('id', data.id);
        }
      }

      toast({
        title: "Sync Started",
        description: `${syncType.charAt(0).toUpperCase() + syncType.slice(1)} sync has been initiated.`,
      });

      return data;
    } catch (error) {
      console.error('Error starting sync:', error);
      toast({
        title: "Sync Failed",
        description: error instanceof Error ? error.message : 'Failed to start sync',
        variant: "destructive",
      });
      return null;
    }
  }, [user?.id, toast]);

  // Cancel an active sync
  const cancelSync = useCallback(async (syncId: string) => {
    try {
      const { error } = await supabase
        .from('sync_status')
        .update({
          status: 'failed',
          error_message: 'Cancelled by user',
          completed_at: new Date().toISOString(),
        })
        .eq('id', syncId);

      if (error) throw error;

      toast({
        title: "Sync Cancelled",
        description: "The sync operation has been cancelled.",
      });
    } catch (error) {
      console.error('Error cancelling sync:', error);
      toast({
        title: "Error",
        description: "Failed to cancel sync",
        variant: "destructive",
      });
    }
  }, [toast]);

  // Set up real-time subscriptions
  useEffect(() => {
    if (!user?.id) return;

    // Initial fetch
    fetchSyncStatus();

    // Subscribe to sync status changes
    const syncChannel = supabase
      .channel('sync-status-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sync_status',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('Sync status update:', payload);
          
          const newRecord = payload.new as SyncStatus;
          const eventType = payload.eventType;

          if (eventType === 'INSERT' || eventType === 'UPDATE') {
            setState(prev => {
              const isActiveSync = ['pending', 'in_progress'].includes(newRecord.status);
              
              // Update current sync if it's active
              const currentSync = isActiveSync ? newRecord : 
                (prev.currentSync?.id === newRecord.id ? null : prev.currentSync);

              // Update history
              const existingIndex = prev.syncHistory.findIndex(sync => sync.id === newRecord.id);
              let syncHistory = [...prev.syncHistory];
              
              if (existingIndex >= 0) {
                syncHistory[existingIndex] = newRecord;
              } else {
                syncHistory.unshift(newRecord);
                syncHistory = syncHistory.slice(0, 10); // Keep only last 10
              }

              return {
                ...prev,
                currentSync,
                syncHistory,
              };
            });

            // Show completion toast
            if (newRecord.status === 'completed') {
              toast({
                title: "Sync Completed",
                description: `Successfully processed ${newRecord.processed_activities} activities.`,
              });
            } else if (newRecord.status === 'failed' && newRecord.error_message) {
              toast({
                title: "Sync Failed",
                description: newRecord.error_message,
                variant: "destructive",
              });
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(syncChannel);
    };
  }, [user?.id, fetchSyncStatus, toast]);

  return {
    ...state,
    startSync,
    cancelSync,
    refetch: fetchSyncStatus,
  };
};